import React from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import loreCornerDetail from '../../assets/lore/lore_details.png';
import loreButton from '../../assets/lore/lore_button.png';
import buttonBuy from '../../assets/button_buy.png';
import cardbackImg from '../../assets/cardback.png';
import teethIcon from '../../assets/teeth_icon.png';
import keyIcon from '../../assets/key_icon.png';
import potionIcon from '../../assets/potion_icon.png';
import torchIcon from '../../assets/torch_icon.png';

function CornerDetail({ className = '', rotationClass = '' }) {
  return (
    <img
      src={loreCornerDetail}
      alt=""
      aria-hidden="true"
      draggable={false}
      className={`pointer-events-none absolute h-[52px] w-[52px] select-none opacity-80 ${rotationClass} ${className}`}
    />
  );
}

function BuyButton({ onClick, disabled = false }) {
  return (
    <div className="relative h-[62px] w-[112px] shrink-0 sm:h-[68px] sm:w-[120px]">
      <img
        src={buttonBuy}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
      <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className="absolute inset-0 appearance-none border-0 bg-transparent p-0 text-white outline-none transition hover:opacity-90 active:scale-[0.99] disabled:cursor-default disabled:opacity-45"
        style={{ fontFamily: 'Karma, serif' }}
      >
        <span className="flex h-full w-full items-center justify-center text-[16px] font-semibold translate-y-[1px] sm:text-[18px]">
          Buy
        </span>
      </button>
    </div>
  );
}

function FooterButton({ label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="relative h-[68px] w-[250px] appearance-none border-0 bg-transparent p-0 text-white shadow-none outline-none transition hover:opacity-90 active:scale-[0.99]"
      style={{ fontFamily: 'Karma, serif' }}
    >
      <img
        src={loreButton}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="pointer-events-none absolute inset-0 h-full w-full select-none object-contain"
      />
      <span className="relative z-[1] flex h-full w-full items-center justify-center text-[18px] font-semibold translate-y-[2px]">
        {label}
      </span>
    </button>
  );
}

function clampStyle(lines = 2) {
  return {
    display: '-webkit-box',
    WebkitLineClamp: lines,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  };
}

function TeethDisplay({ value }) {
  return (
    <div className="mt-3 flex items-center justify-center gap-2 text-white/90">
      <img
        src={teethIcon}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="h-[22px] w-[22px] select-none object-contain sm:h-[24px] sm:w-[24px]"
      />
      <span className="text-[20px] font-semibold leading-none text-white sm:text-[22px]">{value}</span>
    </div>
  );
}

function SupplyLabel({ icon, label }) {
  return (
    <div className="flex items-center gap-2">
      <img
        src={icon}
        alt=""
        aria-hidden="true"
        draggable={false}
        className="h-[18px] w-[18px] shrink-0 select-none object-contain sm:h-[20px] sm:w-[20px]"
      />
      <span>{label}</span>
    </div>
  );
}

export function MerchantModal({
  open,
  onClose,
  teeth,
  torches,
  maxTorches = 3,
  keys,
  maxKeys = 3,
  potions,
  maxPotions = 3,
  cardsForSale = [],
  trinketsForSale = [],
  cardPrice = 2,
  trinketPrice = 4,
  onBuyCard,
  onBuyTrinket,
  onBuyTorch,
  onBuyKey,
  onBuyPotion,
  noMerchant = false,
}) {
  const hasCards = Array.isArray(cardsForSale) && cardsForSale.length > 0;
  const hasTrinkets = Array.isArray(trinketsForSale) && trinketsForSale.length > 0;
  const torchCap = Number.isFinite(maxTorches) ? Math.max(0, maxTorches) : 3;
  const keyCap = Number.isFinite(maxKeys) ? Math.max(0, maxKeys) : 3;
  const potionCap = Number.isFinite(maxPotions) ? Math.max(0, maxPotions) : 3;
  const defaultCardAspect = '5 / 7';
  const [cardAspectById, setCardAspectById] = React.useState({});

  React.useEffect(() => {
    if (!open) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose?.();
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center overflow-hidden p-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onMouseDown={() => onClose?.()}
        >
          <motion.div
            className="relative z-[1] w-[820px] max-w-[94vw] bg-black/80 px-6 pb-8 pt-5 text-white shadow-[0_20px_60px_rgba(0,0,0,0.72)] sm:px-8 sm:pb-10 sm:pt-6"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.98 }}
            transition={{ duration: 0.18, ease: 'easeOut' }}
            onMouseDown={(e) => e.stopPropagation()}
            style={{ fontFamily: 'Karma, serif' }}
          >
            <CornerDetail className="left-4 top-4" rotationClass="rotate-180" />
            <CornerDetail className="right-4 top-4" rotationClass="-rotate-90" />
            <CornerDetail className="left-4 bottom-4" rotationClass="rotate-90" />
            <CornerDetail className="right-4 bottom-4" />

            <div className="relative z-[1] mx-auto flex max-h-[82svh] w-full max-w-[720px] flex-col items-center pt-6 sm:pt-8">
              <h2 className="text-center text-[18px] font-semibold text-white sm:text-[20px]">
                The Merchant
              </h2>

              {noMerchant ? (
                <>
                  <div className="mt-4 w-full max-w-[560px] text-center text-[18px] leading-[1.3] text-white/90">
                    “There is no merchant on this floor.”
                  </div>

                  <TeethDisplay value={teeth} />

                  <div className="mt-8 flex w-full justify-center">
                    <FooterButton label="Leave" onClick={onClose} />
                  </div>
                </>
              ) : (
                <>
                  <div className="mt-4 w-full max-w-[620px] text-center text-[18px] leading-[1.25] text-white/90">
                    “Torches, keys... and a few curiosities for the right price.”
                  </div>

                  <TeethDisplay value={teeth} />

                  <div
                    className="mt-5 min-h-0 w-full flex-1 overflow-y-auto pr-1 overscroll-contain"
                    style={{ touchAction: 'pan-y' }}
                    onWheelCapture={(e) => e.stopPropagation()}
                    onTouchStartCapture={(e) => e.stopPropagation()}
                    onTouchMoveCapture={(e) => e.stopPropagation()}
                  >
                    <div className="space-y-5">
                      <div>
                        <div className="mb-2 text-[18px] font-semibold text-white sm:text-[20px]">
                          Supplies:
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-3 rounded-[18px] border border-white/10 bg-black/90 px-4 py-3 sm:px-5">
                            <div className="min-w-0">
                              <div className="text-[18px] font-semibold text-white sm:text-[20px]">
                                <SupplyLabel icon={torchIcon} label="Torch" />
                              </div>
                              <div className="mt-1 text-[15px] leading-[1.2] text-white/80">
                                Cost: 1 tooth
                              </div>
                              <div className="text-[15px] leading-[1.2] text-white/80">
                                Owned: {torches}/{torchCap}
                              </div>
                            </div>

                            <BuyButton
                              disabled={teeth < 1 || torches >= torchCap}
                              onClick={onBuyTorch}
                            />
                          </div>

                          <div className="flex items-center justify-between gap-3 rounded-[18px] border border-white/10 bg-black/90 px-4 py-3 sm:px-5">
                            <div className="min-w-0">
                              <div className="text-[18px] font-semibold text-white sm:text-[20px]">
                                <SupplyLabel icon={keyIcon} label="Key" />
                              </div>
                              <div className="mt-1 text-[15px] leading-[1.2] text-white/80">
                                Cost: 1 tooth
                              </div>
                              <div className="text-[15px] leading-[1.2] text-white/80">
                                Owned: {keys}/{keyCap}
                              </div>
                            </div>

                            <BuyButton
                              disabled={teeth < 1 || keys >= keyCap}
                              onClick={onBuyKey}
                            />
                          </div>

                          <div className="flex items-center justify-between gap-3 rounded-[18px] border border-white/10 bg-black/90 px-4 py-3 sm:px-5">
                            <div className="min-w-0">
                              <div className="text-[18px] font-semibold text-white sm:text-[20px]">
                                <SupplyLabel icon={potionIcon} label="Potion" />
                              </div>
                              <div className="mt-1 text-[15px] leading-[1.2] text-white/80">
                                Cost: 1 tooth
                              </div>
                              <div className="text-[15px] leading-[1.2] text-white/80">
                                Owned: {potions}/{potionCap}
                              </div>
                            </div>

                            <BuyButton
                              disabled={teeth < 1 || potions >= potionCap}
                              onClick={onBuyPotion}
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <div className="mb-2 flex items-end justify-between gap-3">
                          <div className="text-[18px] font-semibold text-white sm:text-[20px]">
                            Learn something new:
                          </div>
                          <div className="text-right text-sm text-white/60">
                            {cardPrice} teeth each
                          </div>
                        </div>

                        {!hasCards ? (
                          <div className="rounded-[18px] border border-white/10 bg-black/90 px-4 py-4 text-sm text-white/70 sm:px-5">
                            The merchant is rummaging through his satchel...
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {cardsForSale.map((c) => (
                              <div
                                key={c.id}
                                className="flex items-center justify-between gap-3 rounded-[18px] border border-white/10 bg-black/90 px-4 py-3 sm:px-5"
                              >
                                <div className="min-w-0 flex items-center gap-3 sm:gap-4">
                                  <div
                                    className="h-[96px] w-[68px] shrink-0 overflow-hidden rounded-[10px] border border-white/10 bg-black/40 sm:h-[104px] sm:w-[74px]"
                                    style={{ aspectRatio: cardAspectById[c.id] || defaultCardAspect }}
                                  >
                                    <img
                                      src={c.img || cardbackImg}
                                      alt={c.title}
                                      loading="lazy"
                                      decoding="async"
                                      draggable={false}
                                      onLoad={(e) => {
                                        const { naturalWidth, naturalHeight } = e.currentTarget || {};
                                        if (!naturalWidth || !naturalHeight) return;
                                        const ratio = `${naturalWidth} / ${naturalHeight}`;
                                        setCardAspectById((prev) =>
                                          prev[c.id] === ratio ? prev : { ...prev, [c.id]: ratio }
                                        );
                                      }}
                                      className="h-full w-full object-contain"
                                    />
                                  </div>

                                  <div className="min-w-0">
                                    <div className="text-[18px] font-semibold leading-tight text-white sm:text-[20px]">
                                      {c.title}
                                    </div>

                                    {(c.subtitle || c.tag || c.kind) && (
                                      <div className="mt-1 text-[15px] leading-[1.2] text-white/80" style={clampStyle(1)}>
                                        {c.subtitle || c.tag || c.kind}
                                      </div>
                                    )}

                                    {c.description && (
                                      <div
                                        className="mt-1 text-[13px] leading-[1.3] text-white/55 sm:text-[14px]"
                                        style={clampStyle(2)}
                                        title={c.description}
                                      >
                                        {c.description}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <BuyButton
                                  disabled={teeth < cardPrice}
                                  onClick={() => onBuyCard?.(c.id)}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div>
                        <div className="mb-2 flex items-end justify-between gap-3">
                          <div className="text-[18px] font-semibold text-white sm:text-[20px]">
                            Curiosities:
                          </div>
                          <div className="text-right text-sm text-white/60">
                            {trinketPrice} teeth each
                          </div>
                        </div>

                        {!hasTrinkets ? (
                          <div className="rounded-[18px] border border-white/10 bg-black/90 px-4 py-4 text-sm text-white/70 sm:px-5">
                            “Nothing else today... unless you come back later.”
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {trinketsForSale.map((r) => (
                              <div
                                key={r.id}
                                className="flex items-center justify-between gap-3 rounded-[18px] border border-white/10 bg-black/90 px-4 py-3 sm:px-5"
                              >
                                <div className="min-w-0 flex items-center gap-3 sm:gap-4">
                                  <div className="flex h-[56px] w-[56px] shrink-0 items-center justify-center overflow-hidden rounded-[10px] border border-white/10 bg-black/40 sm:h-[64px] sm:w-[64px]">
                                    {r.image ? (
                                      <img
                                        src={r.image}
                                        alt={r.name}
                                        loading="lazy"
                                        decoding="async"
                                        draggable={false}
                                        className="h-full w-full object-contain"
                                      />
                                    ) : (
                                      <div className="text-white/60">?</div>
                                    )}
                                  </div>

                                  <div className="min-w-0">
                                    <div className="text-[18px] font-semibold leading-tight text-white sm:text-[20px]">
                                      {r.name}
                                    </div>

                                    <div className="mt-1 text-[11px] uppercase tracking-widest text-white/50" style={clampStyle(1)}>
                                      {r?.rarity === 'rare'
                                        ? 'Rare'
                                        : r?.rarity === 'uncommon'
                                          ? 'Uncommon'
                                          : 'Common'}
                                    </div>

                                    {r.description && (
                                      <div
                                        className="mt-1 text-[13px] leading-[1.3] text-white/55 sm:text-[14px]"
                                        style={clampStyle(2)}
                                        title={r.description}
                                      >
                                        {r.description}
                                      </div>
                                    )}
                                  </div>
                                </div>

                                <BuyButton
                                  disabled={teeth < trinketPrice}
                                  onClick={() => onBuyTrinket?.(r.id)}
                                />
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex w-full justify-center">
                    <FooterButton label="Leave" onClick={onClose} />
                  </div>
                </>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
